/** Keep native link behaviour, but do not turn a drag, swipe or text selection into navigation. */
export function guardCardNavigation(container: HTMLElement, selector: string) {
  const events = new AbortController();
  const signal = events.signal;
  let gesture: { link: HTMLAnchorElement; id: number; x: number; y: number; moved: boolean } | undefined;
  let suppressed: HTMLAnchorElement | undefined;
  let selectionBeforePointer = '';
  const linkAt = (target: EventTarget | null) => target instanceof Element
    ? target.closest<HTMLAnchorElement>(selector) : null;
  const update = (event: PointerEvent) => {
    if (gesture?.id === event.pointerId && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 10) gesture.moved = true;
  };
  container.addEventListener('pointerdown', event => {
    const link = linkAt(event.target);
    if (!link || !container.contains(link) || !event.isPrimary || event.button !== 0) return;
    suppressed = undefined;
    selectionBeforePointer = container.ownerDocument.getSelection()?.toString() || '';
    gesture = { link, id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
  }, { signal });
  container.ownerDocument.addEventListener('pointermove', update, { passive: true, signal });
  container.ownerDocument.addEventListener('pointerup', event => {
    if (gesture?.id !== event.pointerId) return;
    update(event);
    suppressed = gesture.moved ? gesture.link : undefined;
    gesture = undefined;
  }, { signal });
  container.ownerDocument.addEventListener('pointercancel', event => {
    if (gesture?.id !== event.pointerId) return;
    suppressed = gesture.link;
    gesture = undefined;
  }, { signal });
  container.addEventListener('scroll', () => { if (gesture) gesture.moved = true; }, { capture: true, passive: true, signal });
  container.addEventListener('click', event => {
    const link = linkAt(event.target);
    if (!link || event.detail === 0) return; // Keyboard and assistive-technology activation stay native.
    const selection = container.ownerDocument.getSelection();
    const selecting = selection && !selection.isCollapsed && selection.toString() !== selectionBeforePointer &&
      (link.contains(selection.anchorNode) || link.contains(selection.focusNode));
    if (suppressed === link || selecting) event.preventDefault();
    suppressed = undefined;
  }, { capture: true, signal });
  return () => events.abort();
}
